import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { adminUsers, adminListings, userApi } from '../api/client';
import type { Listing } from '../api/client';
import { formatPrice } from '../utils/format';


export default function Dashboard() {
  const { t } = useTranslation();
  const { profile, refreshProfile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  const [userCount, setUserCount] = useState<number | null>(null);
  const [listingCount, setListingCount] = useState<number | null>(null);
  const [apiAuthError, setApiAuthError] = useState(false);

  const [myAds, setMyAds] = useState<Listing[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);

  const [editingProfile, setEditingProfile] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<'saved' | 'error' | null>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setPhone(profile.phone || '');
    }
  }, [profile]);

  useEffect(() => {
    setApiAuthError(false);
    if (!profile) return;

    const is401 = (e: unknown) => (e instanceof Error && (e.message?.includes('Unauthorized') || e.message?.includes('401'))) || String(e).includes('401');

    if (isAdmin) {
      const fetchWithRetry = <T,>(fn: () => Promise<T>, setResult: (v: T) => void, setZero: () => void) => {
        fn()
          .then(setResult)
          .catch((e) => {
            if (is401(e)) {
              setTimeout(() => {
                fn().then(setResult).catch((e2) => {
                  setZero();
                  if (is401(e2)) setApiAuthError(true);
                });
              }, 400);
            } else {
              setZero();
            }
          });
      };
      const fetchCounts = () => {
        fetchWithRetry(adminUsers.list, (r) => setUserCount(r.items.length), () => setUserCount(0));
        fetchWithRetry(adminListings.listAll.bind(null, { limit: '50' }), (r) => setListingCount(r.items.length), () => setListingCount(0));
      };
      const t = setTimeout(fetchCounts, 150);
      return () => clearTimeout(t);
    }

    if (profile?.orgId) {
      setAdsLoading(true);
      const t = setTimeout(() => {
        adminListings.listOrg(profile.orgId, { limit: '100' })
          .then((r) => setMyAds(r.items || []))
          .catch((e) => {
            if (is401(e)) setApiAuthError(true);
          })
          .finally(() => setAdsLoading(false));
      }, 150);
      return () => clearTimeout(t);
    }
  }, [isAdmin, profile?.orgId, profile]);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      await userApi.updateMe({ name: name.trim() || undefined, phone: phone.trim() || undefined });
      await refreshProfile();
      setProfileMsg('saved');
      setEditingProfile(false);
    } catch {
      setProfileMsg('error');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="dash-page">
        <p style={{ color: '#6b7280' }}>{t('dashboard.loading')}</p>
      </div>
    );
  }

  const authBanner = apiAuthError && (
    <div style={{ padding: '1rem 1.25rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
      <strong>{t('dashboard.apiUnauthorized')}</strong>
      <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem' }}>
        {t('dashboard.apiUnauthorizedHelp')}
      </p>
      <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
        <li><code style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>VITE_API_BASE_URL</code></li>
        <li><code style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>VITE_COGNITO_USER_POOL_ID</code></li>
        <li><code style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>VITE_COGNITO_CLIENT_ID</code></li>
      </ul>
      <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>{t('dashboard.apiUnauthorizedHelp')}</p>
    </div>
  );

  if (isAdmin) {
    const navItems: { to: string; title: string; desc: string; icon: string }[] = [
      { to: '/dashboard/profile', title: t('dashboard.myProfile'), desc: 'Edit your name and phone', icon: '👤' },
      { to: '/dashboard/listings', title: t('dashboard.listingsByOrg'), desc: 'Manage listings by organization', icon: '📋' },
      { to: '/dashboard/all-listings', title: t('dashboard.allAds'), desc: t('dashboard.allAdsAdminHint'), icon: '📑' },
      { to: '/dashboard/users', title: t('dashboard.userManagement'), desc: 'Create users, roles, reset passwords', icon: '👥' },
      { to: '/dashboard/settings', title: t('dashboard.settings'), desc: 'App-wide limits and options', icon: '⚙️' },
      { to: '/dashboard/reports', title: t('dashboard.reports'), desc: 'Stats and analytics', icon: '📊' },
      { to: '/dashboard/leads', title: t('dashboard.leadsInbox'), desc: 'Inquiries and leads', icon: '✉️' },
    ];
    return (
      <div className="dash-page">
        {authBanner}
        <header className="dash-page-header">
          <h1 className="dash-page-title">{t('dashboard.adminDashboard')}</h1>
          <p className="dash-page-desc">{t('dashboard.adminDesc')}</p>
        </header>

        {(userCount !== null || listingCount !== null) && (
          <div className="dash-stats">
            {userCount !== null && (
              <div className="dash-stat-card">
                <div className="dash-stat-label">{t('dashboard.users')}</div>
                <div className="dash-stat-value">{userCount}</div>
              </div>
            )}
            {listingCount !== null && (
              <div className="dash-stat-card">
                <div className="dash-stat-label">{t('dashboard.listings')}</div>
                <div className="dash-stat-value">{listingCount}</div>
              </div>
            )}
          </div>
        )}

        <nav className="dash-nav-grid" aria-label="Admin navigation">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className="dash-nav-card">
              <span className="dash-nav-card-icon" aria-hidden>{item.icon}</span>
              <div>
                <h2 className="dash-nav-card-title">{item.title}</h2>
                <p className="dash-nav-card-desc">{item.desc}</p>
              </div>
            </Link>
          ))}
        </nav>
      </div>
    );
  }

  const statusBadgeClass = (status: string) => {
    if (status === 'published') return 'dash-badge dash-badge--success';
    if (status === 'pending_review') return 'dash-badge dash-badge--warning';
    if (status === 'unpublished') return 'dash-badge dash-badge--danger';
    return 'dash-badge dash-badge--neutral';
  };

  return (
    <div className="dash-page">
      {authBanner}
      <header className="dash-page-header">
        <h1 className="dash-page-title">{t('dashboard.myDashboard')}</h1>
        <p className="dash-page-desc">{t('dashboard.myDashboardDesc')}</p>
      </header>

      <section className="dash-card">
        <div className="dash-card-header">
          <h2 className="dash-card-title">{t('dashboard.myProfile')}</h2>
          {!editingProfile && (
            <button type="button" onClick={() => setEditingProfile(true)} className="dash-btn dash-btn--secondary dash-btn--sm">
              {t('common.edit')}
            </button>
          )}
        </div>
        <div className="dash-card-body">
          {editingProfile ? (
            <form onSubmit={handleProfileSave}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
                <div className="dash-form-group">
                  <label className="dash-form-label">{t('dashboard.name')}</label>
                  <input type="text" className="dash-form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('dashboard.yourNamePlaceholder')} />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">{t('dashboard.phone')}</label>
                  <input type="tel" className="dash-form-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971..." />
                </div>
              </div>
              {profileMsg === 'saved' && <div className="dash-alert dash-alert--success">{t('dashboard.savedSuccess')}</div>}
              {profileMsg === 'error' && <div className="dash-alert dash-alert--error">{t('dashboard.saveFailed')}</div>}
              <div className="dash-modal-footer" style={{ border: 'none', padding: 0 }}>
                <button type="submit" disabled={saving} className="dash-btn dash-btn--primary">{saving ? t('dashboard.saving') : t('profile.save')}</button>
                <button type="button" onClick={() => { setEditingProfile(false); setName(profile.name || ''); setPhone(profile.phone || ''); }} className="dash-btn dash-btn--secondary">{t('form.cancel')}</button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1.25rem' }}>
              <div><div className="dash-stat-label">{t('dashboard.email')}</div><div style={{ fontWeight: 500 }}>{profile.email}</div></div>
              <div><div className="dash-stat-label">{t('dashboard.name')}</div><div style={{ fontWeight: 500 }}>{profile.name || '—'}</div></div>
              <div><div className="dash-stat-label">{t('dashboard.phone')}</div><div style={{ fontWeight: 500 }}>{profile.phone || '—'}</div></div>
              <div><div className="dash-stat-label">{t('dashboard.accountType')}</div><div style={{ fontWeight: 500 }}>{profile.role === 'admin' ? t('dashboard.admin') : t('dashboard.customer')}</div></div>
            </div>
          )}
        </div>
      </section>

      <section className="dash-card">
        <div className="dash-card-header">
          <div>
            <h2 className="dash-card-title">{t('dashboard.myAds')}</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>{t('dashboard.onlyAdminsPublish')}</p>
          </div>
          <Link to={`/dashboard/listings/new?orgId=${profile.orgId}`} className="dash-btn dash-btn--primary">
            + {t('dashboard.addNewAd')}
          </Link>
        </div>
        <div className="dash-card-body">
          {adsLoading ? (
            <p style={{ color: '#6b7280' }}>{t('dashboard.loadingAds')}</p>
          ) : myAds.length === 0 ? (
            <div className="dash-empty">{t('dashboard.noAdsYet')}</div>
          ) : (
            <>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>{t('listingsManager.title')}</th>
                      <th>{t('common.type')}</th>
                      <th>{t('common.status')}</th>
                      <th style={{ textAlign: 'right' }}>{t('common.price')}</th>
                      <th>{t('dashboard.location')}</th>
                      <th style={{ textAlign: 'right' }}>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myAds.map((ad) => (
                      <tr key={ad.listingId}>
                        <td style={{ fontWeight: 500 }}>{ad.titleEn}</td>
                        <td style={{ textTransform: 'capitalize' }}>{ad.type}</td>
                        <td><span className={statusBadgeClass(ad.status)}>{ad.status === 'pending_review' ? t('listingsManager.pendingReview') : ad.status === 'published' ? t('form.published') : ad.status === 'draft' ? t('form.draft') : ad.status === 'unpublished' ? t('form.unpublished') : ad.status}</span></td>
                        <td style={{ textAlign: 'right' }}>{formatPrice(ad.price, ad.currency)}</td>
                        <td style={{ color: '#6b7280' }}>{ad.location?.city || '—'}{ad.location?.area ? `, ${ad.location.area}` : ''}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Link to={`/listings/${ad.listingId}`} style={{ color: 'var(--color-primary)', marginRight: 8 }}>{t('common.view')}</Link>
                          <Link to={`/dashboard/listings/edit/${ad.listingId}?orgId=${ad.orgId}`} style={{ color: '#2563eb' }}>{t('common.edit')}</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                <Link to="/dashboard/listings" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-primary)' }}>{t('dashboard.viewAllAds')}</Link>
              </div>
            </>
          )}
          <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <Link to="/dashboard/leads" className="dash-btn dash-btn--secondary" style={{ textDecoration: 'none' }}>{t('dashboard.leadsInbox')}</Link>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>{t('dashboard.inboxDescription')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
